import { Button } from "@repo/ui/components/ui/button"
import { Calendar } from "@repo/ui/components/ui/calendar"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@repo/ui/components/ui/popover"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@repo/ui/components/ui/select"
import { DateRangePreset } from "@repo/ui/lib/date-presets"
import { getPresetDateRange } from "@repo/ui/lib/date-presets"
import { useDateFnsLocale } from "@repo/ui/lib/i18n"
import { cn } from "@repo/ui/lib/utils"
import { format } from "date-fns"
import { CalendarIcon, X } from "lucide-react"
import { useState } from "react"
import { DateRange } from "react-day-picker"
import { useTranslations } from "use-intl"

interface DateRangePickerProps {
  value: DateRange | undefined
  onChange: (range: DateRange | undefined) => void
  placeholder?: string
  numberOfMonths?: number
  showWeekNumber?: boolean
  presets?: DateRangePreset[]
  requireApply?: boolean
  className?: string
  align?: "start" | "center" | "end"
  clearable?: boolean
}

function normalizeRange(range: DateRange | undefined): DateRange | undefined {
  if (!range) return undefined
  const from = range.from ? new Date(range.from) : undefined
  const to = range.to ? new Date(range.to) : undefined
  if (from) from.setHours(12, 0, 0, 0)
  if (to) to.setHours(12, 0, 0, 0)
  return { from, to }
}

function DateRangePicker({
  value,
  onChange,
  placeholder,
  numberOfMonths = 2,
  showWeekNumber = true,
  presets,
  requireApply = false,
  className,
  align = "start",
  clearable = false,
}: DateRangePickerProps) {
  const t = useTranslations("ui")
  const dateFnsLocale = useDateFnsLocale()
  const [open, setOpen] = useState(false)
  const [pendingRange, setPendingRange] = useState<DateRange | undefined>(value)

  const displayRange = requireApply ? pendingRange : value
  const activeRange = requireApply ? pendingRange : value

  const handleSelect = (range: DateRange | undefined) => {
    const normalized = normalizeRange(range)
    if (requireApply) {
      setPendingRange(normalized)
    } else {
      onChange(normalized)
    }
  }

  const handlePresetChange = (presetValue: string) => {
    const range = getPresetDateRange(presetValue)
    if (!range) return
    const normalized = normalizeRange(range)
    if (requireApply) {
      setPendingRange(normalized)
    } else {
      onChange(normalized)
      setOpen(false)
    }
  }

  const handleApply = () => {
    onChange(pendingRange)
    setOpen(false)
  }

  const handleCancel = () => {
    setPendingRange(value)
    setOpen(false)
  }

  const handleClear = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation()
    onChange(undefined)
  }

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      setPendingRange(value)
    }
    setOpen(nextOpen)
  }

  const formatDate = (date: Date) => {
    try {
      return format(date, "LLL dd, y", { locale: dateFnsLocale })
    } catch {
      return ""
    }
  }

  return (
    <div className="grid gap-2">
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className={cn(
              "h-8 justify-start text-left font-normal",
              !displayRange && "text-muted-foreground",
              className,
            )}
          >
            {displayRange?.from ? (
              displayRange.to ? (
                <>
                  {formatDate(displayRange.from)} -{" "}
                  {formatDate(displayRange.to)}
                </>
              ) : (
                formatDate(displayRange.from)
              )
            ) : (
              <span>{placeholder ?? t("common.chooseDate")}</span>
            )}

            <div className="ml-auto flex items-center gap-1">
              {clearable && displayRange?.from && (
                <span
                  role="button"
                  tabIndex={0}
                  className="rounded-sm p-0.5 hover:bg-muted-foreground/20"
                  onClick={handleClear}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") handleClear(e)
                  }}
                  aria-label={t("dateRangePicker.clearRange")}
                >
                  <X className="h-3 w-3" />
                </span>
              )}
              <CalendarIcon className="h-4 w-4" />
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-2" align={align}>
          {presets && presets.length > 0 && (
            <Select onValueChange={handlePresetChange}>
              <SelectTrigger>
                <SelectValue placeholder={t("dateRangePicker.choosePeriod")} />
              </SelectTrigger>
              <SelectContent position="popper">
                {presets.map((preset) => (
                  <SelectItem key={preset.value} value={preset.value}>
                    {t(`datePresets.${preset.value}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          <Calendar
            initialFocus
            mode="range"
            defaultMonth={activeRange?.from}
            selected={activeRange}
            onSelect={handleSelect}
            numberOfMonths={numberOfMonths}
            weekStartsOn={1}
            showWeekNumber={showWeekNumber}
            locale={dateFnsLocale}
          />

          {requireApply && (
            <div className="flex gap-2 mt-2 pt-2 border-t">
              <Button
                variant="outline"
                size="sm"
                onClick={handleCancel}
                className="flex-1"
              >
                {t("common.cancel")}
              </Button>
              <Button
                size="sm"
                onClick={handleApply}
                disabled={!pendingRange?.from || !pendingRange?.to}
                className="flex-1"
              >
                {t("dateRangePicker.apply")}
              </Button>
            </div>
          )}
        </PopoverContent>
      </Popover>
    </div>
  )
}
DateRangePicker.displayName = "DateRangePicker"

export { DateRangePicker }
export type { DateRangePickerProps }
